import { useCallback, useEffect, useState } from 'react';
import type { ChangeEvent } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Alert,
  AlertDescription,
  Button,
  EmptyState,
  Input,
  ReportTable,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  type ReportTableColumn,
  type ReportTablePaginationState,
  type ReportTableSort,
} from '@roger/ui-components';
import { ArrowLeft, Download, Loader2 } from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import {
  parseAsBoolean,
  parseAsInteger,
  parseAsString,
  useQueryStates,
} from 'nuqs';
import { getVisitTypeDisplayName } from '../hooks/useVisitTypeOptions';
import {
  createReportSortParser,
  parseAsReportDate,
  parseAsReportSortDirection,
} from '../hooks/reportParams/reportQueryParsers';
import { PermissionSlug } from '../constants/featureFlags';
import {
  exportOpenOasisReportReportsOpenOasisExportGet,
  getOpenOasisReportOptionsReportsOpenOasisOptionsGet,
  getOpenOasisReportReportsOpenOasisGet,
} from '../services/api/sdk.gen';
import type {
  OpenOasisReportOptions,
  OpenOasisReportRow,
  OpenOasisSortBy,
  OpenOasisSortDirection,
} from '../services/api/types.gen';
import { downloadResponseAsFile } from '../utils/downloadFile';
import {
  getBusinessErrorMessage,
  getUserFacingApiMessageAsync,
} from '../utils/apiErrorDetail';
import {
  formatDateToISO,
  formatDateToMMDDYYYY,
  parseISOToLocalDate,
} from '../utils/dateUtils';
import { coerceReportPageSize, displayDash } from '../utils/reportDisplay';
import ReportsPermissionAlert from './ReportsPermissionAlert';
import { useReportsBackHref } from '../reporting/backHref';
import { useStore } from '../store';

const ALL_CLINICS_VALUE = '__all_clinics__';
const ALL_STATUSES_VALUE = '__all_statuses__';
const ALL_VISIT_TYPES_VALUE = '__all_visit_types__';
const ALL_CLINICIANS_VALUE = '__all_clinicians__';
const UNASSIGNED_CLINICIAN_VALUE = '__unassigned__';
const ALL_PAYOR_TYPES_VALUE = '__all_payor_types__';

const OPEN_OASIS_SORT_KEYS = [
  'clinic_name',
  'patient',
  'mrn',
  'visit_type',
  'target_date',
  'days_outstanding',
  'status',
  'assigned_clinician',
  'episode_payor',
  'payor_type',
  'notes',
] as const;

const openOasisParsers = {
  clinic: parseAsString.withDefault(''),
  q: parseAsString.withDefault(''),
  status: parseAsString.withDefault(''),
  visit_type: parseAsString.withDefault(''),
  clinician: parseAsString.withDefault(''),
  unassigned: parseAsBoolean.withDefault(false),
  payor_type: parseAsString.withDefault(''),
  days_min: parseAsInteger,
  days_max: parseAsInteger,
  from: parseAsReportDate,
  to: parseAsReportDate,
  sort: createReportSortParser(OPEN_OASIS_SORT_KEYS).withDefault(
    'days_outstanding'
  ),
  dir: parseAsReportSortDirection.withDefault('desc'),
  page: parseAsInteger.withDefault(1),
  size: parseAsInteger.withDefault(25),
};

const formatDateParam = (value: Date | null): string | null =>
  value ? formatDateToISO(value) : null;

const validDaysValue = (value: number | null): number | null =>
  value !== null && Number.isInteger(value) && value >= 0 ? value : null;

const parseDaysInput = (value: string): number | null => {
  if (!value) return null;
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed < 0) return null;
  return parsed;
};

const formatDaysInput = (value: number | null): string =>
  value === null ? '' : String(value);

const patientName = (row: OpenOasisReportRow) =>
  `${row.patient_last_name}, ${row.patient_first_name}`;

const openOasisColumns: ReportTableColumn<OpenOasisReportRow>[] = [
  {
    id: 'clinic_name',
    header: 'Clinic',
    accessor: (row: OpenOasisReportRow) => displayDash(row.clinic_name),
    enableSorting: true,
    minWidth: 160,
  },
  {
    id: 'patient',
    header: 'Patient',
    accessor: patientName,
    enableSorting: true,
    minWidth: 190,
  },
  {
    id: 'mrn',
    header: 'MRN',
    accessor: (row: OpenOasisReportRow) => displayDash(row.mrn),
    enableSorting: true,
    minWidth: 120,
  },
  {
    id: 'visit_type',
    header: 'OASIS',
    accessor: (row: OpenOasisReportRow) =>
      getVisitTypeDisplayName(row.visit_type),
    enableSorting: true,
    minWidth: 190,
  },
  {
    id: 'target_date',
    header: 'Target Date',
    accessor: (row: OpenOasisReportRow) =>
      formatDateToMMDDYYYY(row.target_date),
    enableSorting: true,
    minWidth: 130,
  },
  {
    id: 'days_outstanding',
    header: 'Days Outstanding',
    accessor: (row: OpenOasisReportRow) => row.days_outstanding,
    align: 'right' as const,
    enableSorting: true,
    minWidth: 150,
  },
  {
    id: 'status',
    header: 'Status',
    accessor: (row: OpenOasisReportRow) => row.status.replace(/_/g, ' '),
    enableSorting: true,
    minWidth: 180,
  },
  {
    id: 'assigned_clinician',
    header: 'Currently Assigned',
    accessor: (row: OpenOasisReportRow) =>
      displayDash(row.assigned_clinician_name),
    enableSorting: true,
    minWidth: 190,
  },
  {
    id: 'episode_payor',
    header: 'Episode Payor',
    accessor: (row: OpenOasisReportRow) => displayDash(row.episode_payor_name),
    enableSorting: true,
    minWidth: 180,
  },
  {
    id: 'payor_type',
    header: 'Payor Type',
    accessor: (row: OpenOasisReportRow) =>
      row.episode_payor_type
        ? row.episode_payor_type.replace(/_/g, ' ')
        : 'Unclassified',
    enableSorting: true,
    minWidth: 170,
  },
  {
    id: 'notes',
    header: 'Notes',
    accessor: (row: OpenOasisReportRow) => displayDash(row.notes),
    render: (value, row) => (
      <span className='line-clamp-2 max-w-sm' title={row.notes ?? undefined}>
        {String(value)}
      </span>
    ),
    enableSorting: true,
    minWidth: 240,
  },
];

type OpenOasisQueryState = ReturnType<
  typeof useQueryStates<typeof openOasisParsers>
>[0];
type SetOpenOasisQueryState = ReturnType<
  typeof useQueryStates<typeof openOasisParsers>
>[1];

export function OpenOasisReportWithQueryState({
  showBackLink = true,
}: {
  showBackLink?: boolean;
}) {
  const [params, setParams] = useQueryStates(openOasisParsers, {
    history: 'replace',
    clearOnDefault: true,
  });

  return (
    <OpenOasisReport
      params={params}
      setParams={setParams}
      showBackLink={showBackLink}
    />
  );
}

export default function OpenOasisReportPage() {
  const { hasPermission } = useStore(
    useShallow(state => ({ hasPermission: state.hasPermission }))
  );

  if (!hasPermission(PermissionSlug.ROGER_EMR_REPORTS_ACCESS)) {
    return <ReportsPermissionAlert />;
  }

  return (
    <div className='flex h-full flex-col'>
      <div className='flex-1 overflow-auto px-6 py-4'>
        <OpenOasisReportWithQueryState />
      </div>
    </div>
  );
}

export function OpenOasisReport({
  params,
  setParams,
  showBackLink = true,
}: {
  params: OpenOasisQueryState;
  setParams: SetOpenOasisQueryState;
  showBackLink?: boolean;
}) {
  const [options, setOptions] = useState<OpenOasisReportOptions | null>(null);
  const [rows, setRows] = useState<OpenOasisReportRow[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  const location = useLocation();
  const reportsBackHref = useReportsBackHref();
  const page = params.page > 0 ? params.page : 1;
  const pageSize = coerceReportPageSize(params.size);
  const sortBy = params.sort as OpenOasisSortBy;
  const sortDirection = params.dir as OpenOasisSortDirection;
  const paramsDaysMin = validDaysValue(params.days_min);
  const paramsDaysMax = validDaysValue(params.days_max);
  const [daysMinText, setDaysMinText] = useState(() =>
    formatDaysInput(paramsDaysMin)
  );
  const [daysMaxText, setDaysMaxText] = useState(() =>
    formatDaysInput(paramsDaysMax)
  );
  const [debouncedDaysMin, setDebouncedDaysMin] = useState(paramsDaysMin);
  const [debouncedDaysMax, setDebouncedDaysMax] = useState(paramsDaysMax);
  const [hasEditedDays, setHasEditedDays] = useState(false);
  const pendingDaysMin = parseDaysInput(daysMinText);
  const pendingDaysMax = parseDaysInput(daysMaxText);
  const isDaysDebouncing =
    pendingDaysMin !== debouncedDaysMin || pendingDaysMax !== debouncedDaysMax;
  const hasInvalidDateRange = Boolean(params.from) !== Boolean(params.to);

  const updateReportParams = useCallback(
    (updates: Partial<typeof params>, resetPage = true) => {
      void setParams(resetPage ? { ...updates, page: 1 } : updates);
    },
    [setParams]
  );

  useEffect(() => {
    setDaysMinText(formatDaysInput(paramsDaysMin));
    setDaysMaxText(formatDaysInput(paramsDaysMax));
    setDebouncedDaysMin(paramsDaysMin);
    setDebouncedDaysMax(paramsDaysMax);
  }, [paramsDaysMax, paramsDaysMin]);

  useEffect(() => {
    const hasInvalidUrlDays =
      (location.search.includes('days_min') &&
        typeof params.days_min === 'number' &&
        !Number.isNaN(params.days_min) &&
        params.days_min !== paramsDaysMin) ||
      (location.search.includes('days_max') &&
        typeof params.days_max === 'number' &&
        !Number.isNaN(params.days_max) &&
        params.days_max !== paramsDaysMax);
    if (!hasInvalidUrlDays && !isDaysDebouncing) return;

    const timeoutId = window.setTimeout(() => {
      setDebouncedDaysMin(pendingDaysMin);
      setDebouncedDaysMax(pendingDaysMax);
      updateReportParams(
        {
          days_min: pendingDaysMin,
          days_max: pendingDaysMax,
        },
        hasEditedDays
      );
    }, 300);
    return () => window.clearTimeout(timeoutId);
  }, [
    hasEditedDays,
    isDaysDebouncing,
    location.search,
    params.days_max,
    params.days_min,
    paramsDaysMax,
    paramsDaysMin,
    pendingDaysMax,
    pendingDaysMin,
    updateReportParams,
  ]);

  const updateDaysMin = (value: string) => {
    setHasEditedDays(true);
    const nextMin = parseDaysInput(value);
    const currentMax = parseDaysInput(daysMaxText);
    setDaysMinText(formatDaysInput(nextMin));
    if (nextMin !== null && currentMax !== null && nextMin > currentMax) {
      setDaysMaxText(String(nextMin));
    }
  };

  const updateDaysMax = (value: string) => {
    setHasEditedDays(true);
    const nextMax = parseDaysInput(value);
    const currentMin = parseDaysInput(daysMinText);
    setDaysMaxText(formatDaysInput(nextMax));
    if (nextMax !== null && currentMin !== null && nextMax < currentMin) {
      setDaysMinText(String(nextMax));
    }
  };

  useEffect(() => {
    let cancelled = false;
    getOpenOasisReportOptionsReportsOpenOasisOptionsGet()
      .then(response => {
        if (cancelled) return;
        if (response.error || !response.data) {
          setError('Unable to load Open OASIS report. Please try again.');
          return;
        }
        setOptions(response.data);
      })
      .catch(error => {
        console.error('Failed to load Open OASIS options', error);
        if (!cancelled)
          setError('Unable to load Open OASIS report. Please try again.');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (hasInvalidDateRange) {
      setIsLoading(false);
      setRows([]);
      setTotal(0);
      setError('Select both From and To target dates, or clear both.');
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    getOpenOasisReportReportsOpenOasisGet({
      query: {
        clinic_id: params.clinic || undefined,
        q: params.q || undefined,
        statuses: params.status || undefined,
        visit_types: params.visit_type || undefined,
        assigned_clinician_id: params.clinician || undefined,
        unassigned: params.unassigned || false,
        payor_types: params.payor_type || undefined,
        days_outstanding_min: debouncedDaysMin ?? undefined,
        days_outstanding_max: debouncedDaysMax ?? undefined,
        target_date_from: formatDateParam(params.from),
        target_date_to: formatDateParam(params.to),
        sort_by: sortBy,
        sort_direction: sortDirection,
        page,
        page_size: pageSize,
      },
    })
      .then(response => {
        if (cancelled) return;
        if (response.error || !response.data) {
          setError(
            getBusinessErrorMessage({
              response: {
                status: response.response?.status,
                data: response.error,
              },
            }) ?? 'Unable to load Open OASIS report. Please try again.'
          );
          setRows([]);
          setTotal(0);
          return;
        }
        setRows(response.data.items);
        setTotal(response.data.total);
      })
      .catch(error => {
        console.error('Failed to load Open OASIS report', error);
        if (!cancelled) {
          setError(
            getBusinessErrorMessage(error) ??
              'Unable to load Open OASIS report. Please try again.'
          );
          setRows([]);
          setTotal(0);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    debouncedDaysMax,
    debouncedDaysMin,
    hasInvalidDateRange,
    page,
    pageSize,
    params.clinic,
    params.clinician,
    params.days_max,
    params.days_min,
    params.from,
    params.payor_type,
    params.q,
    params.status,
    params.to,
    params.unassigned,
    params.visit_type,
    sortBy,
    sortDirection,
  ]);

  const sorting: ReportTableSort = { id: sortBy, direction: sortDirection };
  const pagination: ReportTablePaginationState = { page, pageSize };

  const download = async () => {
    if (hasInvalidDateRange) {
      setError('Select both From and To target dates, or clear both.');
      return;
    }
    setIsExporting(true);
    try {
      const response = await exportOpenOasisReportReportsOpenOasisExportGet({
        query: {
          clinic_id: params.clinic || undefined,
          q: params.q || undefined,
          statuses: params.status || undefined,
          visit_types: params.visit_type || undefined,
          assigned_clinician_id: params.clinician || undefined,
          unassigned: params.unassigned || false,
          payor_types: params.payor_type || undefined,
          days_outstanding_min: debouncedDaysMin ?? undefined,
          days_outstanding_max: debouncedDaysMax ?? undefined,
          target_date_from: formatDateParam(params.from),
          target_date_to: formatDateParam(params.to),
          sort_by: sortBy,
          sort_direction: sortDirection,
        },
        responseType: 'blob' as const,
      });
      if (response.error || !response.data) {
        setError(
          await getUserFacingApiMessageAsync(
            {
              response: {
                status: response.response?.status,
                data: response.response?.data ?? response.error,
              },
            },
            'Unable to download Open OASIS report. Please try again.'
          )
        );
        return;
      }
      downloadResponseAsFile(response as never, 'open-oasis-report.xlsx');
    } catch (error) {
      console.error('Failed to download Open OASIS report', error);
      setError(
        await getUserFacingApiMessageAsync(
          error,
          'Unable to download Open OASIS report. Please try again.'
        )
      );
    } finally {
      setIsExporting(false);
    }
  };

  const emptyState = error ? (
    <EmptyState role='alert' title={error} description='Please try again.' />
  ) : (
    <EmptyState
      title='No incomplete OASIS visits match these filters'
      description='Adjust filters and try again.'
    />
  );

  return (
    <div className='space-y-4'>
      {showBackLink && (
        <Link
          to={reportsBackHref}
          className='inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground'
        >
          <ArrowLeft className='h-4 w-4' /> Reports
        </Link>
      )}
      <div className='flex items-start justify-between gap-4'>
        {showBackLink ? (
          <div>
            <h1 className='text-lg font-semibold'>Open OASIS</h1>
            <p className='text-sm text-muted-foreground'>
              Track incomplete OASIS work and iQIES upload readiness.
            </p>
          </div>
        ) : (
          <div />
        )}
        <Button
          variant='outline'
          size='sm'
          onClick={() => void download()}
          disabled={
            isLoading || isExporting || hasInvalidDateRange || isDaysDebouncing
          }
        >
          {isExporting ? (
            <Loader2 className='h-4 w-4 animate-spin' />
          ) : (
            <Download className='h-4 w-4' />
          )}
          Export
        </Button>
      </div>
      {error ? (
        <Alert className='border-red-200 bg-red-50'>
          <AlertDescription className='text-red-800'>{error}</AlertDescription>
        </Alert>
      ) : null}
      <div className='space-y-3 rounded-lg border bg-card p-4'>
        <div className='flex flex-wrap items-end gap-3'>
          <label
            className='space-y-1 text-sm font-medium'
            htmlFor='open-oasis-clinic-filter'
          >
            Clinic
            <Select
              value={params.clinic || ALL_CLINICS_VALUE}
              onValueChange={value =>
                updateReportParams({
                  clinic: value === ALL_CLINICS_VALUE ? '' : value,
                })
              }
            >
              <SelectTrigger id='open-oasis-clinic-filter' selectSize='default'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_CLINICS_VALUE}>All clinics</SelectItem>
                {options?.clinics.map(clinic => (
                  <SelectItem key={clinic.id} value={clinic.id}>
                    {clinic.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
          <label className='space-y-1 text-sm font-medium'>
            Search
            <Input
              value={params.q}
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                updateReportParams({ q: event.target.value || '' })
              }
              placeholder='Name or MRN'
            />
          </label>
          <label
            className='space-y-1 text-sm font-medium'
            htmlFor='open-oasis-status-filter'
          >
            Status
            <Select
              value={params.status || ALL_STATUSES_VALUE}
              onValueChange={value =>
                updateReportParams({
                  status: value === ALL_STATUSES_VALUE ? '' : value,
                })
              }
            >
              <SelectTrigger id='open-oasis-status-filter' selectSize='default'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_STATUSES_VALUE}>All statuses</SelectItem>
                {options?.statuses.map(status => (
                  <SelectItem key={status.value} value={status.value}>
                    {status.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
          <label
            className='space-y-1 text-sm font-medium'
            htmlFor='open-oasis-visit-type-filter'
          >
            Visit Type
            <Select
              value={params.visit_type || ALL_VISIT_TYPES_VALUE}
              onValueChange={value =>
                updateReportParams({
                  visit_type: value === ALL_VISIT_TYPES_VALUE ? '' : value,
                })
              }
            >
              <SelectTrigger
                id='open-oasis-visit-type-filter'
                selectSize='default'
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_VISIT_TYPES_VALUE}>
                  All visit types
                </SelectItem>
                {options?.visit_types.map(visitType => (
                  <SelectItem key={visitType.value} value={visitType.value}>
                    {visitType.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
          <label
            className='space-y-1 text-sm font-medium'
            htmlFor='open-oasis-clinician-filter'
          >
            Clinician
            <Select
              value={
                params.unassigned
                  ? UNASSIGNED_CLINICIAN_VALUE
                  : params.clinician || ALL_CLINICIANS_VALUE
              }
              onValueChange={value => {
                updateReportParams({
                  clinician:
                    value === ALL_CLINICIANS_VALUE ||
                    value === UNASSIGNED_CLINICIAN_VALUE
                      ? ''
                      : value,
                  unassigned: value === UNASSIGNED_CLINICIAN_VALUE,
                });
              }}
            >
              <SelectTrigger
                id='open-oasis-clinician-filter'
                selectSize='default'
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_CLINICIANS_VALUE}>
                  All clinicians
                </SelectItem>
                <SelectItem value={UNASSIGNED_CLINICIAN_VALUE}>
                  Unassigned
                </SelectItem>
                {options?.assigned_clinicians.map(clinician => (
                  <SelectItem key={clinician.id} value={clinician.id}>
                    {clinician.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
          <label
            className='space-y-1 text-sm font-medium'
            htmlFor='open-oasis-payor-filter'
          >
            Payor Type
            <Select
              value={params.payor_type || ALL_PAYOR_TYPES_VALUE}
              onValueChange={value =>
                updateReportParams({
                  payor_type: value === ALL_PAYOR_TYPES_VALUE ? '' : value,
                })
              }
            >
              <SelectTrigger id='open-oasis-payor-filter' selectSize='default'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_PAYOR_TYPES_VALUE}>
                  All payor types
                </SelectItem>
                {options?.payor_types.map(payorType => (
                  <SelectItem key={payorType.value} value={payorType.value}>
                    {payorType.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
        </div>
        <div className='flex flex-wrap items-end gap-3'>
          <label className='space-y-1 text-sm font-medium'>
            From
            <Input
              type='date'
              value={formatDateParam(params.from) ?? ''}
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                updateReportParams({
                  from: event.target.value
                    ? parseISOToLocalDate(event.target.value)
                    : null,
                })
              }
            />
          </label>
          <label className='space-y-1 text-sm font-medium'>
            To
            <Input
              type='date'
              value={formatDateParam(params.to) ?? ''}
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                updateReportParams({
                  to: event.target.value
                    ? parseISOToLocalDate(event.target.value)
                    : null,
                })
              }
            />
          </label>
          <label className='space-y-1 text-sm font-medium'>
            Days Min
            <Input
              type='number'
              min={0}
              step={1}
              inputMode='numeric'
              value={daysMinText}
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                updateDaysMin(event.target.value)
              }
            />
          </label>
          <label className='space-y-1 text-sm font-medium'>
            Days Max
            <Input
              type='number'
              min={0}
              step={1}
              inputMode='numeric'
              value={daysMaxText}
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                updateDaysMax(event.target.value)
              }
            />
          </label>
        </div>
      </div>

      <ReportTable
        data={rows}
        columns={openOasisColumns}
        getRowId={row => row.visit_id}
        caption='Incomplete OASIS visits'
        sorting={sorting}
        onSortingChange={next => {
          updateReportParams(
            next
              ? { sort: next.id as OpenOasisSortBy, dir: next.direction }
              : { sort: 'days_outstanding', dir: 'desc' }
          );
        }}
        isLoading={isLoading}
        emptyState={emptyState}
        pagination={{
          ...pagination,
          total,
          totalPages: Math.ceil(total / pageSize),
        }}
        onPaginationChange={next => {
          void setParams({ page: next.page, size: next.pageSize });
        }}
      />
    </div>
  );
}
